import { ArrowLeftIcon } from 'assets/icons';
import { ButtonStyle } from 'common/button';
import { CreateVaultSteps, NotificationType } from 'common/enums';
import { ModalWarning } from 'common/modal-warning';
import { appActions, AppContext } from 'context';
import { trackCustomEvent } from 'helpers/analytics';
import {
  checkAllFieldsAndGetErrors,
  hasErrorsInformationSection,
} from 'helpers/new-vault-helper';
import { NotificationObject } from 'models/notificationObject';
import { VaultsController } from 'networking/controllers/vaults-controller';
import { captureEvent } from 'posthog';
import React, { useContext, useEffect, useReducer, useState } from 'react';
import { goToPage, RouteName } from 'routes';
import { ProgressSteps } from './components/progress-steps';
import { StepConfirmation } from './components/step-confirmation';
import { StepInformation } from './components/step-information';
import { StepPlan } from './components/step-plan';
import { WizardFooter } from './components/wizard-footer';
import { VaultNewinitialState, VaultNewReducer } from './vault-new-reducer';
import styles from './vault-new.module.scss';

const STEP_HEADINGS: Record<
  CreateVaultSteps,
  { title: string; description: React.ReactNode }
> = {
  [CreateVaultSteps.information]: {
    title: 'Create new Vault',
    description:
      'Start with a name. You can add collaborators and a cover photo anytime.',
  },
  [CreateVaultSteps.plan]: {
    title: 'Choose a plan',
    description: (
      <>
        Most cloud storage is rented. With Forever Storage, your family can{' '}
        <em>own</em> this Vault—and keep it for generations.
      </>
    ),
  },
  [CreateVaultSteps.confirmation]: {
    title: 'Review and create',
    description: 'Check everything over before your vault is created.',
  },
};

const VaultNew: React.FC = () => {
  const [newVaultState, newVaultDispatch] = useReducer(
    VaultNewReducer,
    VaultNewinitialState,
  );
  const { dispatch: generalDispatch } = useContext(AppContext);

  const [showWarning, setShowWarning] = useState(false);
  const [creating, setCreating] = useState(false);
  const [plans, setPlans] = useState<StoragePlanType[]>([]);
  const [plansLoading, setPlansLoading] = useState(true);

  const { step, planSelected } = newVaultState;

  useEffect(() => {
    let cancelled = false;

    VaultsController.getStoragePlans()
      .then((fetched) => {
        if (!cancelled) setPlans(fetched);
      })
      .catch(() => {
        if (!cancelled) setPlans([]);
      })
      .finally(() => {
        if (!cancelled) setPlansLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  // Creates the vault (in `awaiting_first_payment`) and hands off to Stripe's
  // hosted checkout. The vault exists before payment so the session has a real
  // id to return to, and an abandoned checkout leaves a resumable draft.
  const handleCreateVault = async () => {
    if (!planSelected || creating) return;
    setCreating(true);

    try {
      const requestData: VaultNewRequestType = {
        ...newVaultState.vault,
        // Legacy subscription machinery still requires a type; storage-plan
        // vaults ride on 'standard'.
        subscriptionType: 'standard',
        planKey: planSelected.id,
      };

      const vaultResponse = await VaultsController.createVault(requestData);
      const checkoutUrl = await VaultsController.createCheckoutSession(
        vaultResponse.id,
      );

      // Full-page navigation, not the router: this leaves the app for Stripe.
      window.location.assign(checkoutUrl);
    } catch (err) {
      generalDispatch({
        type: appActions.NOTIFICATION,
        notification: new NotificationObject({
          show: true,
          title: 'Create Vault',
          message: "We couldn't start checkout. Please try again.",
          type: NotificationType.Error,
        }),
      });
    } finally {
      setCreating(false);
    }
  };

  const handleNextAction = () => {
    if (step === CreateVaultSteps.information) {
      const newErrorsList = checkAllFieldsAndGetErrors(newVaultState.vault);
      newVaultDispatch({ type: 'UPDATE_ERRORS', errors: newErrorsList });

      if (hasErrorsInformationSection(newErrorsList)) return;

      captureEvent('createVaultNext');
      trackCustomEvent('createVaultNext');
      newVaultDispatch({ type: 'MOVE_STEP', direction: 'next' });
      return;
    }

    if (step === CreateVaultSteps.plan) {
      if (!planSelected) return;

      captureEvent('createNewVault');
      newVaultDispatch({ type: 'MOVE_STEP', direction: 'next' });
      return;
    }

    handleCreateVault();
  };

  const handlePreviousAction = () => {
    if (step === CreateVaultSteps.information) {
      trackCustomEvent('startVaultCreationCancel');
      setShowWarning(true);
      return;
    }

    newVaultDispatch({ type: 'MOVE_STEP', direction: 'back' });
  };

  const showCancelWarning = () => (
    <ModalWarning
      title="Your progress will be lost"
      content="If you leave this page without saving your vault won't be created and your progress will be lost. Are you sure you want to continue?"
      successButtonText="Confirm"
      successStyleButton={ButtonStyle.RedFilled}
      closeFn={() => setShowWarning(false)}
      successFn={() => {
        trackCustomEvent('confirmVaultCreationCancel');
        goToPage(RouteName.Vaults);
      }}
    />
  );

  const showStepContent = () => {
    switch (step) {
      case CreateVaultSteps.plan:
        return (
          <StepPlan
            plans={plans}
            loading={plansLoading}
            newVaultState={newVaultState}
            newVaultDispatch={newVaultDispatch}
          />
        );

      case CreateVaultSteps.confirmation:
        return (
          <StepConfirmation
            newVaultState={newVaultState}
            onEditStep={(targetStep) =>
              newVaultDispatch({ type: 'GO_TO_STEP', step: targetStep })
            }
          />
        );

      case CreateVaultSteps.information:
      default:
        return (
          <StepInformation
            newVaultState={newVaultState}
            newVaultDispatch={newVaultDispatch}
          />
        );
    }
  };

  return (
    <>
      {showWarning && showCancelWarning()}

      <div className={styles.page}>
        <div className={styles.canvas}>
          <div className={styles.container}>
            <button
              type="button"
              className={styles.backButton}
              onClick={handlePreviousAction}
            >
              <ArrowLeftIcon />
              {step === CreateVaultSteps.information ? 'Cancel' : 'Back'}
            </button>

            <div className={styles.header}>
              <div className={styles.titleBlock}>
                <h1 className={styles.title}>{STEP_HEADINGS[step].title}</h1>
                <p className={styles.description}>
                  {STEP_HEADINGS[step].description}
                </p>
              </div>

              <ProgressSteps
                step={step}
                onStepSelect={(targetStep) =>
                  newVaultDispatch({ type: 'GO_TO_STEP', step: targetStep })
                }
              />
            </div>

            <div className={styles.stepContent}>{showStepContent()}</div>
          </div>
        </div>

        <WizardFooter
          newVaultState={newVaultState}
          loading={creating}
          notifyNextAction={handleNextAction}
        />
      </div>
    </>
  );
};

export { VaultNew };
